import { PRESTIGE, ELDER_COUNCIL, SCALING, WAVE } from '../config';
import { ElderName, ElderBonuses, PrestigeState } from '../types';
import { gameStateManager } from './GameStateManager';

const ELDER_NAMES: ElderName[] = ['newbeginning', 'warrior', 'mason', 'merchant'];

/** Milestone definitions: prestige count → reward */
const MILESTONES: { count: number; id: string; name: string; bonusLegacy?: number }[] = [
  { count: 1, id: 'first_gen', name: 'First Generation' },
  { count: 3, id: 'tribal_memory', name: 'Tribal Memory' },
  { count: 5, id: 'bronze_age', name: 'Bronze Age', bonusLegacy: 5 },
  { count: 10, id: 'iron_age', name: 'Iron Age' },
];

export class PrestigeManager {
  private get prestige(): PrestigeState {
    return gameStateManager.permanent.prestige;
  }

  // ---- Legacy Calculation ----

  calculateLegacy(goldEarnedThisRun: number): number {
    return Math.floor(goldEarnedThisRun / PRESTIGE.legacyDivisor);
  }

  getProjectedLegacy(goldEarnedThisRun: number): number {
    return this.calculateLegacy(goldEarnedThisRun);
  }

  canPrestige(_goldEarnedThisRun: number): boolean {
    return true; // Always available — player chooses when to reset
  }

  // ---- Prestige Action ----

  performPrestige(goldEarnedThisRun: number): { legacyEarned: number; newMilestones: string[] } {
    const legacyEarned = this.calculateLegacy(goldEarnedThisRun);
    this.prestige.totalLegacy += legacyEarned;
    this.prestige.prestigeCount++;

    // Ancestral unit: +1 damage per prestige after milestone 10
    if (this.prestige.prestigeCount >= 10) {
      this.prestige.ancestralUnitDamage += 1;
    }

    // Check milestones
    const newMilestones = this.checkMilestones();

    return { legacyEarned, newMilestones };
  }

  private checkMilestones(): string[] {
    const newlyUnlocked: string[] = [];
    for (const milestone of MILESTONES) {
      if (this.prestige.prestigeCount >= milestone.count &&
        !this.prestige.milestones.includes(milestone.id)) {
        this.prestige.milestones.push(milestone.id);
        newlyUnlocked.push(milestone.id);

        // Award bonus legacy
        if (milestone.bonusLegacy) {
          this.prestige.totalLegacy += milestone.bonusLegacy;
        }
      }
    }
    return newlyUnlocked;
  }

  // ---- Elder Council ----

  getAvailableLegacy(): number {
    return this.prestige.totalLegacy - this.prestige.spentLegacy;
  }

  getElderTier(elder: ElderName): number {
    return this.prestige.elders[elder] ?? 0;
  }

  canPurchaseElderTier(elder: ElderName): boolean {
    const currentTier = this.prestige.elders[elder];
    const tiers = ELDER_COUNCIL[elder].tiers;
    if (currentTier >= tiers.length) return false;
    const nextTier = tiers[currentTier];
    return this.getAvailableLegacy() >= nextTier.cost;
  }

  getNextElderTierCost(elder: ElderName): number {
    const currentTier = this.prestige.elders[elder];
    const tiers = ELDER_COUNCIL[elder].tiers;
    if (currentTier >= tiers.length) return -1;
    return tiers[currentTier].cost;
  }

  purchaseElderTier(elder: ElderName): boolean {
    if (!this.canPurchaseElderTier(elder)) return false;
    const currentTier = this.prestige.elders[elder];
    const cost = ELDER_COUNCIL[elder].tiers[currentTier].cost;
    this.prestige.spentLegacy += cost;
    this.prestige.elders[elder]++;
    return true;
  }

  // ---- Computed Bonuses ----

  getElderBonuses(): ElderBonuses {
    const bonuses: ElderBonuses = {
      // New Beginning
      damageBonusNB: 0,
      repairBonusNB: 0,
      wallHPBonusNB: 0,
      goldBonusNB: 0,
      startWave: 0,
      startUnits: {},
      autoClickInterval: 0,
      unlockWallCrossbowTop: false,
      unlockWallCrossbowBottom: false,
      unlockBoomerangThrower: false,
      // Warrior
      damageMult: 0,
      attackSpeedMult: 0,
      bloodFrenzyEnabled: false,
      berserkerInstinctEnabled: false,
      // Mason
      wallHPMult: 0,
      repairRateMult: 0,
      startWallLevel: 0,
      thornyWallPercent: 0,
      extraWallSlots: 0,
      ironWillEnabled: false,
      wallSlotDamageMult: 0,
      // Merchant
      goldMult: 0,
      startGoldWaveRef: 0,
      buyCostMult: 1,
      chainCostMult: 1,
      bossGoldMultOverride: 0,
      clickGoldMult: 0,
      windfallChance: 0,
      offlineHoursBonus: 0,
    };

    for (const elderName of ELDER_NAMES) {
      const tier = this.prestige.elders[elderName];
      const tiers = ELDER_COUNCIL[elderName].tiers;

      for (let i = 0; i < tier; i++) {
        const effect = tiers[i].effect;
        for (const [key, value] of Object.entries(effect)) {
          switch (key) {
            // Additive bonuses
            case 'damageBonusNB':
            case 'repairBonusNB':
            case 'wallHPBonusNB':
            case 'goldBonusNB':
            case 'damageMult':
            case 'attackSpeedMult':
            case 'wallHPMult':
            case 'repairRateMult':
            case 'goldMult':
            case 'thornyWallPercent':
            case 'extraWallSlots':
            case 'wallSlotDamageMult':
            case 'clickGoldMult':
            case 'windfallChance':
            case 'offlineHoursBonus':
              (bonuses as any)[key] += value as number;
              break;

            // Take highest
            case 'startWallLevel':
            case 'startGoldWaveRef':
            case 'startWave':
              (bonuses as any)[key] = Math.max((bonuses as any)[key], value as number);
              break;

            // Auto-click: take fastest (lowest non-zero interval)
            case 'autoClickInterval':
              if (bonuses.autoClickInterval === 0 || (value as number) < bonuses.autoClickInterval) {
                bonuses.autoClickInterval = value as number;
              }
              break;

            // Multiplicative (multiply together)
            case 'buyCostMult':
            case 'chainCostMult':
              (bonuses as any)[key] *= value as number;
              break;

            // Override (take latest non-zero)
            case 'bossGoldMultOverride':
              (bonuses as any)[key] = value as number;
              break;

            // Boolean flags
            case 'unlockWallCrossbowTop':
              bonuses.unlockWallCrossbowTop = true;
              break;
            case 'unlockWallCrossbowBottom':
              bonuses.unlockWallCrossbowBottom = true;
              break;
            case 'unlockBoomerangThrower':
              bonuses.unlockBoomerangThrower = true;
              break;
            case 'bloodFrenzy':
              bonuses.bloodFrenzyEnabled = true;
              break;
            case 'berserkerInstinct':
              bonuses.berserkerInstinctEnabled = true;
              break;
            case 'ironWill':
              bonuses.ironWillEnabled = true;
              break;

            // Start units: merge (take highest count per unit type)
            case 'startUnits': {
              const units = value as Record<string, number>;
              for (const [unitType, count] of Object.entries(units)) {
                bonuses.startUnits[unitType] = Math.max(bonuses.startUnits[unitType] || 0, count);
              }
              break;
            }
          }
        }
      }
    }

    // Innate per-Legacy bonuses (scaled for large legacy values)
    bonuses.damageMult     += this.prestige.totalLegacy * 0.002;  // +0.2% damage per legacy
    bonuses.goldMult       += this.prestige.totalLegacy * 0.003;  // +0.3% gold per legacy
    bonuses.repairRateMult += this.prestige.totalLegacy * 0.002;  // +0.2% repair per legacy

    return bonuses;
  }

  /** Calculate starting gold for Merchant "Head Start" — full wave's worth of kills at the reference wave */
  getStartingGold(): number {
    const bonuses = this.getElderBonuses();
    if (bonuses.startGoldWaveRef <= 0) return 0;
    const wave = bonuses.startGoldWaveRef;
    const goldPerKill = SCALING.enemyBaseGold * Math.pow(1 + SCALING.enemyGoldGrowth, wave);
    const enemyCount = WAVE.baseEnemyCount + Math.floor(wave * WAVE.enemiesPerWave) + Math.floor(wave / 10) * WAVE.bonusPerMilestone;
    return Math.round(goldPerKill * enemyCount);
  }

  // ---- Status Queries ----

  getPrestigeCount(): number {
    return this.prestige.prestigeCount;
  }

  getTotalLegacy(): number {
    return this.prestige.totalLegacy;
  }

  hasMilestone(id: string): boolean {
    return this.prestige.milestones.includes(id);
  }

  isElderMaxed(elder: ElderName): boolean {
    return this.prestige.elders[elder] >= ELDER_COUNCIL[elder].tiers.length;
  }
}
